import type { Result, AppError } from '../../error/types';
import { isEnvironment } from './isEnvironment';
import { ENVIRONMENTS } from '../constants/ENVIRONMENTS';

/**
 * @group Functions
 * @summary <blockquote>Utility function to check if the current environment is development using the Result pattern.</blockquote>
 * 
 * @returns Result containing true if the current environment is 'development', false otherwise, or AppError if environment detection fails
 * 
 * @example
 * ```typescript
 * import { isDevelopment } from '@shared/utils';
 * 
 * const result = isDevelopment();
 * 
 * result.match({
 *   ok: (isDev) => {
 *     if (isDev) {
 *       console.log('Running in development mode');
 *     }
 *   },
 *   err: (error) => {
 *     console.error(`Environment check failed: ${error.errorMessage}`);
 *   }
 * });
 * ```
 * 
 * @example
 * ```typescript
 * // With environment variables:
 * // NODE_ENV=development → returns Ok(true)
 * // VERCEL_ENV=preview   → returns Ok(false)
 * // VERCEL_ENV=production → returns Ok(false)
 * ```
 */
export function isDevelopment(): Result<boolean, AppError> {
  return isEnvironment(ENVIRONMENTS.DEVELOPMENT);
}